import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { HelpfulPricingGuides } from "@/components/HelpfulPricingGuides";
import { ArrowRight, ClipboardCheck, Settings, MonitorCog, Wrench, Paintbrush, CheckCircle, BatteryCharging, Leaf } from "lucide-react";
import { services } from "@/lib/site-config";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { faqSchema, FAQAccordion } from "@/components/FAQAccordion";
import { ReassuranceBanner } from "@/components/ReassuranceBanner";
import { CTABlock } from "@/components/CTABlock";
import { AnimatedSection, StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { PrimaryCTA } from "@/components/PrimaryCTA";
import { getPublicConfig } from "@/lib/site-content.functions";
import { ServiceTestimonials } from "@/components/ServiceTestimonials";
import { InternalLinksBlock } from "@/components/InternalLinksBlock";

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  ClipboardCheck,
  Settings,
  MonitorCog,
  Wrench,
  Paintbrush,
};

const hubFaqs = [
  { q: "Do you handle every job in-house?", a: "Yes — MOT, servicing, diagnostics, mechanical repairs and bodywork are all done at our Poplar workshop. One garage, one bill, one point of contact." },
  { q: "Can I combine an MOT with a service or repairs?", a: "Yes, and we recommend it. Booking together saves you a trip and we can usually offer a combined price. Mention it when you book." },
  { q: "Which makes and models do you work on?", a: "All makes and models — European, Japanese, Korean and American cars and light commercial vehicles up to 3,500kg." },
  { q: "Do you provide a written quote before starting work?", a: "Always. We diagnose first, give you a clear itemised quote, and only start work once you approve it." },
];

export const Route = createFileRoute("/services")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) => seoHead({
    path: "/services",
    title: "Garage Services East London | MOT & Repairs | Woodseer Autos",
    description: "Full-service garage in Poplar, E14. MOT, car servicing, diagnostics, mechanical repairs and bodywork — all under one roof. Honest pricing, all makes.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Services", path: "/services" },
    ],
    jsonLd: [
      localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
      faqSchema(hubFaqs),
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Woodseer Autos — Services",
        itemListElement: services.map((s, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: `https://woodseerautos.co.uk/${s.slug}`,
          name: s.name,
        })),
      },
      ...services.map((s) => ({
        "@context": "https://schema.org",
        "@type": "Service",
        name: `${s.name} — Woodseer Autos`,
        serviceType: s.name,
        description: s.shortDesc,
        url: `https://woodseerautos.co.uk/${s.slug}`,
        areaServed: ["Poplar", "Canary Wharf", "Limehouse", "Isle of Dogs", "Tower Hamlets", "East London"],
        provider: {
          "@type": "AutoRepair",
          name: "Woodseer Autos",
          telephone: NAP.phoneHref.replace("tel:", ""),
          address: {
            "@type": "PostalAddress",
            streetAddress: "16 Pixley Street",
            addressLocality: "Poplar, London",
            postalCode: "E14 7DF",
            addressCountry: "GB",
          },
        },
      })),
    ],
  }),
  component: ServicesHubPage,
});

function ServicesHubPage() {
  return (
    <>
      <section className="border-b border-border bg-gradient-to-b from-secondary/40 to-background">
        <div className="mx-auto max-w-5xl px-4 py-12 md:py-16 text-center">
          <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-bold text-primary">
            One garage · Everything covered
          </p>
          <h1 className="mt-4 text-3xl md:text-5xl font-bold text-foreground tracking-tight">
            Garage Services in East London
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            MOT, servicing, diagnostics, mechanical repairs and bodywork — all carried out
            at our Poplar workshop. Honest pricing, all makes and models, no upselling.
          </p>
          <div className="mt-2 flex justify-center">
            <PrimaryCTA location="services-hub-hero" intent="quote" variant="light" />
          </div>
        </div>
      </section>

      <ReassuranceBanner />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4">
          <AnimatedSection>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              What we do
            </h2>
            <p className="mt-2 text-muted-foreground">
              Click any service for full details, common issues and FAQs.
            </p>
          </AnimatedSection>

          <StaggerContainer className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((s) => {
              const Icon = ICONS[s.icon] || Wrench;
              return (
                <StaggerItem key={s.slug}>
                  <Link
                    to={`/${s.slug}` as "/mot"}
                    className="group block rounded-xl border border-border bg-card p-5 hover:border-primary/40 hover:bg-secondary/30 transition-all h-full"
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-bold text-foreground group-hover:text-primary transition-colors">
                          {s.name}
                        </h3>
                        <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">
                          {s.shortDesc}
                        </p>
                        <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary">
                          Learn more about {s.name} <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </StaggerItem>
              );
            })}
          </StaggerContainer>

          {/* Featured: EV & Hybrid Servicing */}
          <AnimatedSection>
            <Link
              to="/services/ev-hybrid-servicing"
              className="group mt-6 block rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 via-card to-card p-6 md:p-7 hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 transition-all"
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <BatteryCharging className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold tracking-[0.18em] uppercase text-primary">
                    <Leaf className="h-3 w-3" /> New
                  </p>
                  <h3 className="mt-2 text-lg md:text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                    EV &amp; Hybrid Servicing
                  </h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                    Routine servicing for Tesla, Polestar, MG, Kia, Toyota, Lexus and PHEVs —
                    brakes, tyres, 12V battery, cabin filter and safety checks. Honest about
                    high-voltage work.
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary">
                    Read the EV &amp; hybrid guide
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </AnimatedSection>

          {/* Featured: Air Con Regas */}
          <AnimatedSection>
            <Link
              to="/services/air-con-regas"
              className="group mt-4 block rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 via-card to-card p-6 md:p-7 hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 transition-all"
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <BatteryCharging className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[10px] font-bold tracking-[0.18em] uppercase text-primary">
                    <Leaf className="h-3 w-3" /> New
                  </p>
                  <h3 className="mt-2 text-lg md:text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                    Air Con Regas &amp; Service
                  </h3>
                  <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed max-w-2xl">
                    R134a and R1234yf supported. Free initial leak check, antibacterial treatment,
                    fixed independent-garage price — cheaper than a main dealer.
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-primary">
                    Read the air con guide
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-secondary/20 border-y border-border">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight text-center">
            Why drivers choose Woodseer Autos
          </h2>
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "DVSA-authorised MOT station (V106376)",
              "All work in-house — no sub-contracting",
              "Clear written quotes before any work starts",
              "Parts & labour warranty on repairs",
              "Service book stamped to protect warranty",
              "Trusted by drivers across East London",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                <CheckCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4">
          <FAQAccordion faqs={hubFaqs} title="Frequently asked" />
        </div>
      </section>

      <ServiceTestimonials title="Trusted by East London drivers" />

      <InternalLinksBlock showHubLink={false} />

      <HelpfulPricingGuides />
      <CTABlock />
    </>
  );
}
